import React, { createContext, useContext, useState } from "react";

const ConversionContext = createContext();

export const useConversion = () => {
  const context = useContext(ConversionContext);
  if (!context) {
    throw new Error("useConversion must be used within ConversionProvider");
  }
  return context;
};

export const ConversionProvider = ({ children }) => {
  const [conversionData, setConversionData] = useState(null);

  /**
   * Set conversion data and store in sessionStorage
   * @param {Object} data - Conversion data (formType, timestamp, etc.)
   */
  const setConversion = (data) => {
    const conversionInfo = {
      ...data,
      timestamp: Date.now(),
      converted: true,
    };

    // Store in state
    setConversionData(conversionInfo);

    // Store in sessionStorage as backup
    sessionStorage.setItem("pendingConversion", JSON.stringify(conversionInfo));

    return conversionInfo;
  };

  /**
   * Validate conversion data (check existence and timestamp)
   * @param {Object} data - Conversion data to validate
   * @param {number} maxAge - Max age in milliseconds (default: 5 minutes)
   * @returns {boolean} - Whether conversion is valid
   */
  const isValidConversion = (data, maxAge = 300000) => {
    if (!data || !data.timestamp || !data.converted) {
      return false;
    }

    const age = Date.now() - data.timestamp;
    return age <= maxAge;
  };

  /**
   * Get conversion data from sessionStorage
   * @returns {Object|null} - Conversion data or null
   */
  const getStoredConversion = () => {
    try {
      const stored = sessionStorage.getItem("pendingConversion");
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Error reading stored conversion:", error);
    }
    return null;
  };

  /**
   * Clear conversion data from state and sessionStorage
   */
  const clearConversion = () => {
    setConversionData(null);
    sessionStorage.removeItem("pendingConversion");
  };

  const value = {
    conversionData,
    setConversion,
    isValidConversion,
    getStoredConversion,
    clearConversion,
  };

  return (
    <ConversionContext.Provider value={value}>
      {children}
    </ConversionContext.Provider>
  );
};
